import React, { useState } from 'react';
import { Button, Container, Typography, Box, Grid, Card, CardContent, Modal, IconButton, CircularProgress, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import DrawIcon from '@mui/icons-material/Draw';
import SpeedIcon from '@mui/icons-material/Speed';
import SyncIcon from '@mui/icons-material/Sync';
import DevicesIcon from '@mui/icons-material/Devices';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import WebIcon from '@mui/icons-material/Web';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import Header from '../components/Header';
import SatelliteIcon from '@mui/icons-material/Satellite';
import FenceIcon from '@mui/icons-material/Fence';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../api/firebase';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Turnstile from 'react-turnstile';
import fenceSketchConcept from '../assets/Fence Sketch Concept.png';
import fenceSketch from '../assets/fence sketch example 2.png';
import priceManagement from '../assets/prices.png';
import drawFenceStep from '../assets/Draw Fence Step.png';
import projectManagement from '../assets/leads.jpg';

const LandingPage = () => {
  const theme = useTheme();
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleOpenVideo = () => {
    setVideoModalOpen(true);
  };

  const handleCloseVideo = () => {
    setVideoModalOpen(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    // Reset success state when user starts typing again
    if (submitSuccess) {
      setSubmitSuccess(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    // Validate captcha token
    if (!captchaToken) {
      toast.error('Please complete the security verification');
      return;
    }

    setIsSubmitting(true);

    try {
      // Add to Firestore
      await addDoc(collection(db, 'contactSubmissions'), {
        ...formData,
        timestamp: serverTimestamp(),
        source: 'landing-page'
      });

      // Send email notification via backend API (non-blocking)
      const backendUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:8000' 
        : (process.env.REACT_APP_BACKEND_URL || 'https://api.fencesketch.com');
      fetch(`${backendUrl}/api/notifications/contact-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          captchaToken
        })
      }).catch((error) => {
        console.error('Error sending email notification:', error);
        // Don't show error to user - form submission was successful
      });

      toast.success("Message sent successfully! We'll get back to you soon.");

      // Show success state
      setSubmitSuccess(true);

      // Reset form
      setFormData({
        name: '',
        email: '',
        message: ''
      });
      setCaptchaToken(null);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      {/* Hero Section */}
      <Box
        id="home"
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.brand.brand} 0%, ${theme.palette.interactions.interactive.hover} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          minHeight: '600px',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={4}
            alignItems="center"
          >
            <Grid
              item
              xs={12}
              md={6}
            >
              <Typography
                variant="h1"
                sx={{
                  color: 'white',
                  fontWeight: 800,
                  fontSize: { xs: '2rem', md: '3.5rem' },
                  mb: 3,
                  lineHeight: 1.2
                }}
              >
                Create Professional Fence Site Plans
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  mb: 4,
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  fontWeight: 400
                }}
              >
                Draw accurate fence layouts on Google Earth satellite imagery. Show customers exactly what their fence will look like with professional site
                plans.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  component="a"
                  href="https://portal.fencesketch.com/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    bgcolor: 'white',
                    color: theme.palette.brand.brand,
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.9)'
                    }
                  }}
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleOpenVideo}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Watch Demo
                </Button>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: '400px', md: '600px' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {/* Top/Back Image */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: { xs: 0, md: 20 },
                    left: { xs: '50%', md: 0 },
                    transform: { xs: 'translateX(-50%)', md: 'none' },
                    width: { xs: '90%', md: '85%' },
                    zIndex: 2,
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
                    border: '3px solid rgba(255, 255, 255, 0.2)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: { xs: 'translateX(-50%) translateY(-5px)', md: 'translateY(-5px)' },
                      boxShadow: '0 25px 70px rgba(0, 0, 0, 0.5)'
                    }
                  }}
                >
                  <img
                    src={fenceSketch}
                    alt="Fence Sketch - Application interface"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                </Box>

                {/* Bottom/Front Image - Overlapping */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: { xs: 0, md: 20 },
                    right: { xs: '50%', md: 0 },
                    transform: { xs: 'translateX(50%)', md: 'none' },
                    width: { xs: '90%', md: '85%' },
                    zIndex: 3,
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
                    border: '3px solid rgba(255, 255, 255, 0.2)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: { xs: 'translateX(50%) translateY(-5px)', md: 'translateY(-5px)' },
                      boxShadow: '0 25px 70px rgba(0, 0, 0, 0.5)'
                    }
                  }}
                >
                  <img
                    src={fenceSketchConcept}
                    alt="Fence Sketch - Concept design with fence layout"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container
        maxWidth="lg"
        sx={{ py: 8 }}
        id="features"
      >
        <Typography
          variant="h2"
          align="center"
          sx={{
            color: theme.palette.typography.heading.primary,
            fontWeight: 700,
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            mb: 2
          }}
        >
          Why Choose Fence Sketch?
        </Typography>
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.interactions.subtle.default,
              fontSize: '1.1rem'
            }}
          >
            Everything you need to create professional fence estimates
          </Typography>
        </Box>

        <Grid
          container
          spacing={4}
        >
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <Card
              sx={{
                height: '100%',
                textAlign: 'center',
                p: 3,
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}
            >
              <SatelliteIcon sx={{ fontSize: 60, color: theme.palette.brand.brand, mb: 2 }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                Google Earth Integration
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.interactions.subtle.default }}
              >
                Draw fence layouts directly on Google Earth satellite imagery
              </Typography>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <Card
              sx={{
                height: '100%',
                textAlign: 'center',
                p: 3,
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}
            >
              <FenceIcon sx={{ fontSize: 60, color: theme.palette.brand.brand, mb: 2 }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                Fence-Specific Tools
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.interactions.subtle.default }}
              >
                Purpose-built drawing tools designed specifically for fencing contractors
              </Typography>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <Card
              sx={{
                height: '100%',
                textAlign: 'center',
                p: 3,
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}
            >
              <SyncIcon sx={{ fontSize: 60, color: theme.palette.brand.brand, mb: 2 }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                CRM Integrations
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.interactions.subtle.default }}
              >
                Sync with Jobber and JobNimbus to attach site plans to your quotes
              </Typography>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <Card
              sx={{
                height: '100%',
                textAlign: 'center',
                p: 3,
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}
            >
              <DrawIcon sx={{ fontSize: 60, color: theme.palette.brand.brand, mb: 2 }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 2 }}
              >
                Unlimited Drawings
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.interactions.subtle.default }}
              >
                Create unlimited site plans and share them with your customers
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Detailed Features Section */}
      <Box
        sx={{ bgcolor: '#F8F9FA', py: 8 }}
        id="detailed-features"
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{
              color: theme.palette.typography.heading.primary,
              fontWeight: 700,
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              mb: 2
            }}
          >
            Powerful Features for Fence Contractors
          </Typography>
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.interactions.subtle.default,
                fontSize: '1.1rem'
              }}
            >
              See how Fence Sketch helps you create professional estimates faster
            </Typography>
          </Box>

          {/* Feature 1: Create Detailed Fence Plans */}
          <Grid
            container
            spacing={6}
            alignItems="center"
            sx={{ mb: 8 }}
          >
            <Grid
              item
              xs={12}
              md={6}
            >
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: 4,
                  border: `1px solid ${theme.palette.borders.border}`
                }}
              >
                <img
                  src={fenceSketch}
                  alt="Create detailed fence plans"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block'
                  }}
                />
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
            >
              <Typography
                variant="h4"
                sx={{
                  color: theme.palette.typography.heading.primary,
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  mb: 3
                }}
              >
                Create Detailed Fence Plans
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.interactions.subtle.default,
                    fontSize: '1.1rem',
                    lineHeight: 1.8
                  }}
                >
                  Draw professional fence layouts directly on Google Earth satellite imagery. Create detailed site plans with accurate measurements, property lines, gates, and corners. Your customers will see exactly what they're getting with visual plans that build trust and speed up approvals.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, mt: 0.5 }} />
                  <Typography variant="body1">
                    Draw on Google Earth satellite imagery
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, mt: 0.5 }} />
                  <Typography variant="body1">
                    Accurate measurements and property lines
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, mt: 0.5 }} />
                  <Typography variant="body1">
                    Professional site plans that impress customers
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Feature 2: Track Fence Prices */}
          <Grid
            container
            spacing={6}
            alignItems="center"
            sx={{ mb: 8 }}
          >
            <Grid
              item
              xs={12}
              md={6}
              order={{ xs: 2, md: 1 }}
            >
              <Typography
                variant="h4"
                sx={{
                  color: theme.palette.typography.heading.primary,
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  mb: 3
                }}
              >
                Track Your Fence Prices
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.interactions.subtle.default,
                    fontSize: '1.1rem',
                    lineHeight: 1.8
                  }}
                >
                  Keep track of your fence pricing for different materials, styles, and configurations. Build a pricing database that helps you create accurate quotes faster. Set custom prices for different fence types, materials, and labor costs to ensure consistent and profitable pricing.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, mt: 0.5 }} />
                  <Typography variant="body1">
                    Custom pricing for materials and labor
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, mt: 0.5 }} />
                  <Typography variant="body1">
                    Track prices by fence type and style
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, mt: 0.5 }} />
                  <Typography variant="body1">
                    Build accurate quotes quickly
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              order={{ xs: 1, md: 2 }}
            >
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: 4,
                  border: `1px solid ${theme.palette.borders.border}`
                }}
              >
                <img
                  src={priceManagement}
                  alt="Price Management - Track your fence prices"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block'
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Feature 3: Manage Leads and Projects */}
          <Grid
            container
            spacing={6}
            alignItems="center"
            sx={{ mb: 8 }}
          >
            <Grid
              item
              xs={12}
              md={6}
            >
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: 4,
                  border: `1px solid ${theme.palette.borders.border}`
                }}
              >
                <img
                  src={projectManagement}
                  alt="Manage leads and projects"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block'
                  }}
                />
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
            >
              <Typography
                variant="h4"
                sx={{
                  color: theme.palette.typography.heading.primary,
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  mb: 3
                }}
              >
                Manage Your Leads and Projects
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.interactions.subtle.default,
                    fontSize: '1.1rem',
                    lineHeight: 1.8
                  }}
                >
                  Keep all your fence projects organized in one place. Track leads from initial contact through completion. Attach site plans, quotes, and customer information to each project. Stay organized and never lose track of a potential customer or active job.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, mt: 0.5 }} />
                  <Typography variant="body1">
                    Organize all your projects in one place
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, mt: 0.5 }} />
                  <Typography variant="body1">
                    Track leads from contact to completion
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, mt: 0.5 }} />
                  <Typography variant="body1">
                    Attach site plans and quotes to projects
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Feature 4: Instant Quote Website Widget */}
          <Grid
            container
            spacing={6}
            alignItems="center"
          >
            <Grid
              item
              xs={12}
              md={6}
              order={{ xs: 2, md: 1 }}
            >
              <Typography
                variant="h4"
                sx={{
                  color: theme.palette.typography.heading.primary,
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  mb: 3
                }}
              >
                Instant Quote Website Widget
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.interactions.subtle.default,
                    fontSize: '1.1rem',
                    lineHeight: 1.8
                  }}
                >
                  Embed an interactive quote widget directly on your website. Let customers get instant estimates by drawing their fence on your site. Capture leads automatically and provide instant pricing to potential customers, even when you're not available.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, mt: 0.5 }} />
                  <Typography variant="body1">
                    Embed on your website easily
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, mt: 0.5 }} />
                  <Typography variant="body1">
                    Customers get instant estimates
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, mt: 0.5 }} />
                  <Typography variant="body1">
                    Automatically capture leads 24/7
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              order={{ xs: 1, md: 2 }}
            >
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: 4,
                  border: `1px solid ${theme.palette.borders.border}`
                }}
              >
                <img
                  src={drawFenceStep}
                  alt="Instant Quote Website Widget - Draw fence step"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box
        sx={{ bgcolor: theme.palette.surfaces.background, py: 8 }}
        id="pricing"
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                color: theme.palette.typography.heading.primary,
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '3rem' },
                mb: 3,
                lineHeight: 1.2
              }}
            >
              Simple, Transparent Pricing
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.interactions.subtle.default,
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                fontWeight: 400,
                maxWidth: '700px',
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Everything you need to create professional fence site plans for just $25/month
            </Typography>
          </Box>

          <Grid
            container
            justifyContent="center"
          >
            {/* Single Pricing Plan */}
            <Grid
              item
              xs={12}
              sm={10}
              md={8}
              lg={7}
            >
              <Card
                sx={{
                  p: { xs: 4, md: 6 },
                  textAlign: 'center',
                  border: `3px solid ${theme.palette.brand.brand}`,
                  borderRadius: 3,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  boxShadow: '0 10px 40px rgba(237, 85, 33, 0.15)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 60px rgba(237, 85, 33, 0.25)'
                  }
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, mb: 2, color: theme.palette.typography.heading.primary }}
                >
                  Professional Plan
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Typography
                    component="span"
                    variant="h2"
                    sx={{ fontWeight: 800, color: theme.palette.brand.brand, fontSize: { xs: '3rem', md: '4.5rem' } }}
                  >
                    $25
                  </Typography>
                  <Typography
                    component="span"
                    variant="h5"
                    sx={{ color: theme.palette.interactions.subtle.default, fontWeight: 400, ml: 1 }}
                  >
                    /month
                  </Typography>
                </Box>
                <Box sx={{ mb: 5 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.interactions.subtle.default,
                      fontSize: '1.1rem',
                      fontWeight: 500
                    }}
                  >
                    Visual site planning tool for fence contractors
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    mb: 6,
                    textAlign: 'left',
                    px: { xs: 0, md: 3 }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, fontSize: 26, mt: 0.25, flexShrink: 0 }} />
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, fontSize: '1.05rem', lineHeight: 1.6 }}
                    >
                      Unlimited site plan drawings
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, fontSize: 26, mt: 0.25, flexShrink: 0 }} />
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, fontSize: '1.05rem', lineHeight: 1.6 }}
                    >
                      Google Earth satellite imaging
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, fontSize: 26, mt: 0.25, flexShrink: 0 }} />
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, fontSize: '1.05rem', lineHeight: 1.6 }}
                    >
                      Fence-specific drawing tools
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, fontSize: 26, mt: 0.25, flexShrink: 0 }} />
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, fontSize: '1.05rem', lineHeight: 1.6 }}
                    >
                      Jobber & JobNimbus integrations
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, fontSize: 26, mt: 0.25, flexShrink: 0 }} />
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, fontSize: '1.05rem', lineHeight: 1.6 }}
                    >
                      Attach site plans to your quotes
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, fontSize: 26, mt: 0.25, flexShrink: 0 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, fontSize: '1.05rem', lineHeight: 1.6 }}
                      >
                        Automatic material calculations
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          bgcolor: theme.palette.status.warning.surface,
                          color: theme.palette.status.warning.onSurface,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px'
                        }}
                      >
                        COMING SOON
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, fontSize: 26, mt: 0.25, flexShrink: 0 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, fontSize: '1.05rem', lineHeight: 1.6 }}
                      >
                        Professional PDF quotes
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          bgcolor: theme.palette.status.warning.surface,
                          color: theme.palette.status.warning.onSurface,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          letterSpacing: '0.5px'
                        }}
                      >
                        COMING SOON
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, fontSize: 26, mt: 0.25, flexShrink: 0 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, fontSize: '1.05rem', lineHeight: 1.6 }}
                      >
                        Virtual Estimating Website Plugin
                      </Typography>
                     
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, fontSize: 26, mt: 0.25, flexShrink: 0 }} />
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, fontSize: '1.05rem', lineHeight: 1.6 }}
                    >
                      Priority email support
                    </Typography>
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  component="a"
                  href="https://portal.fencesketch.com/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ py: 2, fontSize: '1.2rem', fontWeight: 700, mb: 2 }}
                >
                  Start 14-Day Free Trial
                </Button>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.interactions.subtle.default }}
                >
                  Credit card required • Cancel anytime
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Integrations Section */}
      <Box
        sx={{ bgcolor: theme.palette.surfaces.background, py: 8 }}
        id="integrations"
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{
              color: theme.palette.typography.heading.primary,
              fontWeight: 700,
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              mb: 2
            }}
          >
            Seamless Integrations
          </Typography>
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.interactions.subtle.default,
                fontSize: '1.1rem'
              }}
            >
              Connect Fence Sketch with your favorite business tools
            </Typography>
          </Box>

          <Grid
            container
            spacing={4}
            justifyContent="center"
          >
            {/* Jobber Integration */}
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
            >
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  p: 4,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  border: `2px solid ${theme.palette.borders.border}`,
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6,
                    borderColor: theme.palette.brand.brand
                  }
                }}
              >
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 60 }}>
                  <img
                    src="https://portal.fencesketch.com/static/media/jobber-logo.c38d57c0616206e8e489.png"
                    alt="Jobber Logo"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '60px',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, mb: 2, color: theme.palette.typography.heading.primary }}
                >
                  Jobber
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.interactions.subtle.default, lineHeight: 1.7 }}
                >
                  Sync your fence sketches directly to Jobber. Attach site plans to quotes and keep all your project information in one place.
                </Typography>
              </Card>
            </Grid>

            {/* JobNimbus Integration */}
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
            >
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  p: 4,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  border: `2px solid ${theme.palette.borders.border}`,
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6,
                    borderColor: theme.palette.brand.brand
                  }
                }}
              >
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 60 }}>
                  <img
                    src="https://dka575ofm4ao0.cloudfront.net/pages-transactional_logos/retina/196734/JN_Logo_Full-Blue.png"
                    alt="JobNimbus Logo"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '60px',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, mb: 2, color: theme.palette.typography.heading.primary }}
                >
                  JobNimbus
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.interactions.subtle.default, lineHeight: 1.7 }}
                >
                  Integrate with JobNimbus to streamline your workflow. Send professional fence sketches directly to your CRM and quotes.
                </Typography>
              </Card>
            </Grid>

            {/* Zapier Integration */}
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
            >
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  p: 4,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  border: `2px solid ${theme.palette.borders.border}`,
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6,
                    borderColor: theme.palette.brand.brand
                  }
                }}
              >
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 60 }}>
                  <img
                    src="https://cdn.worldvectorlogo.com/logos/zapier-2.svg"
                    alt="Zapier Logo"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '60px',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, mb: 2, color: theme.palette.typography.heading.primary }}
                >
                  Zapier
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.interactions.subtle.default, lineHeight: 1.7 }}
                >
                  Connect Fence Sketch to thousands of apps through Zapier. Automate your workflow and save time on every project.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* About Section */}
      <Box
        sx={{ bgcolor: '#F8F9FA', py: 8 }}
        id="about"
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={6}
            alignItems="center"
          >
            <Grid
              item
              xs={12}
              md={6}
            >
              <Typography
                variant="h3"
                sx={{
                  color: theme.palette.typography.heading.primary,
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  mb: 3
                }}
              >
                Built Specifically for Fence Contractors
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  'Draw fence layouts with property lines and measurements',
                  'Automatic material calculations based on your designs',
                  'Custom pricing for different fence types and materials',
                  'Generate PDF quotes and material lists',
                  'Share sketches with customers instantly',
                  'Mobile-friendly for on-site estimates'
                ].map((benefit, index) => (
                  <Box
                    key={index}
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
                  >
                    <CheckCircleIcon sx={{ color: theme.palette.brand.highlight, mt: 0.5 }} />
                    <Typography
                      variant="body1"
                      sx={{ fontSize: '1.05rem' }}
                    >
                      {benefit}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
            >
              <Box
                sx={{
                  bgcolor: 'white',
                  borderRadius: 2,
                  p: 4,
                  boxShadow: 3,
                  textAlign: 'center'
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, mb: 2, color: theme.palette.brand.brand }}
                >
                  Ready to Get Started?
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="body1"
                    sx={{ color: theme.palette.interactions.subtle.default }}
                  >
                    Join hundreds of fence contractors who trust Fence Sketch for their estimating needs.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  component="a"
                  href="https://portal.fencesketch.com/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600
                  }}
                >
                  Start Your Free Trial
                </Button>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: 2, color: theme.palette.interactions.subtle.default }}
                >
                  Credit card required • 14-day free trial
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box
        sx={{ bgcolor: theme.palette.surfaces.background, py: 8 }}
        id="contact"
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            align="center"
            sx={{
              color: theme.palette.typography.heading.primary,
              fontWeight: 700,
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              mb: 2
            }}
          >
            Get in Touch
          </Typography>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.interactions.subtle.default,
                fontSize: '1.1rem'
              }}
            >
              Have questions? We'd love to hear from you.
            </Typography>
          </Box>

          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: 2,
              p: 4,
              boxShadow: 3
            }}
          >
            <Grid
              container
              spacing={3}
            >
              <Grid
                item
                xs={12}
                sm={6}
              >
                <Typography
                  variant="body2"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  Name
                </Typography>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  placeholder="Your name"
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${theme.palette.borders.border}`,
                    borderRadius: '4px',
                    fontSize: '1rem',
                    fontFamily: 'Montserrat, sans-serif'
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
              >
                <Typography
                  variant="body2"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  Email
                </Typography>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  placeholder="your@email.com"
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${theme.palette.borders.border}`,
                    borderRadius: '4px',
                    fontSize: '1rem',
                    fontFamily: 'Montserrat, sans-serif'
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
              >
                <Typography
                  variant="body2"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  Message
                </Typography>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleFormChange}
                  placeholder="Tell us about your needs..."
                  rows={6}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${theme.palette.borders.border}`,
                    borderRadius: '4px',
                    fontSize: '1rem',
                    fontFamily: 'Montserrat, sans-serif',
                    resize: 'vertical'
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
              >
                {/* Cloudflare Turnstile Bot Protection */}
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Turnstile
                    sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY || "0x4AAAAAACMjRhl07ePFQrKK"}
                    onSuccess={(token) => {
                      setCaptchaToken(token);
                    }}
                    onError={(error) => {
                      console.error('Turnstile error:', error);
                      setCaptchaToken(null);
                    }}
                    onExpire={() => {
                      setCaptchaToken(null);
                    }}
                    theme="light"
                  />
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
              >
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleSubmit}
                  disabled={isSubmitting || submitSuccess || !captchaToken}
                  sx={{ 
                    py: 1.5, 
                    fontSize: '1.1rem',
                    ...(submitSuccess && {
                      bgcolor: theme.palette.brand.highlight,
                      '&:hover': {
                        bgcolor: theme.palette.brand.highlight,
                      }
                    })
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <CircularProgress
                        size={24}
                        sx={{ color: 'white', mr: 1 }}
                      />
                      Sending...
                    </>
                  ) : submitSuccess ? (
                    <>
                      <CheckIcon sx={{ mr: 1, fontSize: 24 }} />
                      Message Sent Successfully!
                    </>
                  ) : (
                    'Send Message'
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Grid
            container
            spacing={4}
            sx={{ mt: 6 }}
          >
            <Grid
              item
              xs={12}
              md={4}
              sx={{ textAlign: 'center' }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 1 }}
              >
                Email
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: theme.palette.interactions.subtle.default }}
              >
                support@fencesketch.com
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ textAlign: 'center' }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 1 }}
              >
                Phone
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: theme.palette.interactions.subtle.default }}
              >
                (817) 953-3623
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ textAlign: 'center' }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: 1 }}
              >
                Office Hours
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: theme.palette.interactions.subtle.default }}
              >
                Mon-Fri: 9AM - 5PM CST
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ bgcolor: theme.palette.surfaces.background, py: 8 }} id="faq">
        <Container maxWidth="md">
          <Typography
            variant="h2"
            align="center"
            sx={{
              color: theme.palette.typography.heading.primary,
              fontWeight: 700,
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              mb: 2
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.interactions.subtle.default,
                fontSize: '1.1rem'
              }}
            >
              Everything you need to know about Fence Sketch
            </Typography>
          </Box>

          <Box sx={{ mt: 4 }}>
            <Accordion
              sx={{
                mb: 2,
                boxShadow: 2,
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.brand.brand }} />}
                sx={{
                  bgcolor: 'white',
                  py: 2,
                  '&:hover': {
                    bgcolor: theme.palette.surfaces.surface.hover
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.typography.heading.primary }}>
                  How does Fence Sketch work?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: 'white', pt: 0 }}>
                <Typography variant="body1" sx={{ color: theme.palette.interactions.subtle.default, lineHeight: 1.8 }}>
                  Fence Sketch allows you to draw fence layouts directly on Google Earth satellite imagery. Simply enter an address, use our fence-specific drawing tools to sketch your design, and generate professional site plans that you can share with customers or attach to quotes in Jobber or JobNimbus.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion
              sx={{
                mb: 2,
                boxShadow: 2,
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.brand.brand }} />}
                sx={{
                  bgcolor: 'white',
                  py: 2,
                  '&:hover': {
                    bgcolor: theme.palette.surfaces.surface.hover
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.typography.heading.primary }}>
                  Do I need to install any software?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: 'white', pt: 0 }}>
                <Typography variant="body1" sx={{ color: theme.palette.interactions.subtle.default, lineHeight: 1.8 }}>
                  No installation required! Fence Sketch is a web-based application that works in your browser. You can access it from any device - desktop, tablet, or mobile - as long as you have an internet connection.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion
              sx={{
                mb: 2,
                boxShadow: 2,
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.brand.brand }} />}
                sx={{
                  bgcolor: 'white',
                  py: 2,
                  '&:hover': {
                    bgcolor: theme.palette.surfaces.surface.hover
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.typography.heading.primary }}>
                  Can I use Fence Sketch with my existing CRM?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: 'white', pt: 0 }}>
                <Typography variant="body1" sx={{ color: theme.palette.interactions.subtle.default, lineHeight: 1.8 }}>
                  Yes! Fence Sketch integrates with Jobber and JobNimbus, allowing you to seamlessly attach site plans to your quotes. We also offer Zapier Webhooks integration, so you can connect Fence Sketch to any CRM system. Simply create your fence layout in Fence Sketch and sync it directly to your preferred CRM.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion
              sx={{
                mb: 2,
                boxShadow: 2,
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.brand.brand }} />}
                sx={{
                  bgcolor: 'white',
                  py: 2,
                  '&:hover': {
                    bgcolor: theme.palette.surfaces.surface.hover
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.typography.heading.primary }}>
                  How many site plans can I create?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: 'white', pt: 0 }}>
                <Typography variant="body1" sx={{ color: theme.palette.interactions.subtle.default, lineHeight: 1.8 }}>
                  With your $25/month subscription, you can create unlimited site plans. There are no restrictions on the number of projects or drawings you can create.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion
              sx={{
                mb: 2,
                boxShadow: 2,
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.brand.brand }} />}
                sx={{
                  bgcolor: 'white',
                  py: 2,
                  '&:hover': {
                    bgcolor: theme.palette.surfaces.surface.hover
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.typography.heading.primary }}>
                  Can I cancel my subscription anytime?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: 'white', pt: 0 }}>
                <Typography variant="body1" sx={{ color: theme.palette.interactions.subtle.default, lineHeight: 1.8 }}>
                  Absolutely! You can cancel your subscription at any time with no penalties or fees. Your access will continue until the end of your current billing period.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion
              sx={{
                mb: 2,
                boxShadow: 2,
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.brand.brand }} />}
                sx={{
                  bgcolor: 'white',
                  py: 2,
                  '&:hover': {
                    bgcolor: theme.palette.surfaces.surface.hover
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.typography.heading.primary }}>
                  What features are coming soon?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: 'white', pt: 0 }}>
                <Typography variant="body1" sx={{ color: theme.palette.interactions.subtle.default, lineHeight: 1.8 }}>
                  We're actively developing automatic material calculations, professional PDF quote generation, and a virtual estimating website plugin. These features will be available to all subscribers at no additional cost once released.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion
              sx={{
                mb: 2,
                boxShadow: 2,
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.brand.brand }} />}
                sx={{
                  bgcolor: 'white',
                  py: 2,
                  '&:hover': {
                    bgcolor: theme.palette.surfaces.surface.hover
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.typography.heading.primary }}>
                  Do you offer support?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: 'white', pt: 0 }}>
                <Typography variant="body1" sx={{ color: theme.palette.interactions.subtle.default, lineHeight: 1.8 }}>
                  Yes! We provide email support to all subscribers. You can reach us at support@fencesketch.com. We typically respond within 24 hours during business hours (Mon-Fri: 9AM - 5PM CST).
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: theme.palette.surfaces.darkBackground, color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid
            container
            spacing={4}
          >
            <Grid
              item
              xs={12}
              md={4}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, mb: 2 }}
              >
                Fence Sketch
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
              >
                Professional fence estimating software for contractors who want to work smarter, not harder.
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, mb: 2 }}
              >
                Product
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                  variant="body2"
                  onClick={() => {
                    const element = document.getElementById('features');
                    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  sx={{ color: 'rgba(255, 255, 255, 0.7)', cursor: 'pointer', '&:hover': { color: 'white' } }}
                >
                  Features
                </Typography>
                <Typography
                  variant="body2"
                  onClick={() => {
                    const element = document.getElementById('pricing');
                    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  sx={{ color: 'rgba(255, 255, 255, 0.7)', cursor: 'pointer', '&:hover': { color: 'white' } }}
                >
                  Pricing
                </Typography>
                <Typography
                  variant="body2"
                  onClick={() => {
                    const element = document.getElementById('about');
                    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  sx={{ color: 'rgba(255, 255, 255, 0.7)', cursor: 'pointer', '&:hover': { color: 'white' } }}
                >
                  About
                </Typography>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, mb: 2 }}
              >
                Company
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                  variant="body2"
                  onClick={() => {
                    const element = document.getElementById('about');
                    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  sx={{ color: 'rgba(255, 255, 255, 0.7)', cursor: 'pointer', '&:hover': { color: 'white' } }}
                >
                  About
                </Typography>
                <Typography
                  variant="body2"
                  onClick={() => {
                    const element = document.getElementById('contact');
                    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  sx={{ color: 'rgba(255, 255, 255, 0.7)', cursor: 'pointer', '&:hover': { color: 'white' } }}
                >
                  Contact
                </Typography>
                <Typography
                  variant="body2"
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  sx={{ color: 'rgba(255, 255, 255, 0.7)', cursor: 'pointer', '&:hover': { color: 'white' } }}
                >
                  Back to Top
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid rgba(255, 255, 255, 0.1)', textAlign: 'center' }}>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
            >
              © 2026 Fence Sketch. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Video Modal */}
      <Modal
        open={videoModalOpen}
        onClose={handleCloseVideo}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: { xs: '90%', sm: '80%', md: '70%', lg: '60%' },
            maxWidth: '1000px',
            bgcolor: 'black',
            borderRadius: 2,
            outline: 'none',
            boxShadow: 24
          }}
        >
          <IconButton
            onClick={handleCloseVideo}
            sx={{
              position: 'absolute',
              top: -40,
              right: -40,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.8)'
              },
              zIndex: 1
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              position: 'relative',
              paddingTop: '56.25%', // 16:9 aspect ratio
              width: '100%'
            }}
          >
            <iframe
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: '8px'
              }}
              src="https://www.youtube.com/embed/YOUR_VIDEO_ID"
              title="Fence Sketch Demo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </Box>
        </Box>
      </Modal>

      {/* Toast Notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default LandingPage;
